import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { settingService } from '../../services/settingService';
import { fleetService } from '../../services/fleetService';
import DispatchSettingsForm from '../../components/settings/DispatchSettingsForm';
import PaymentSettingsForm from '../../components/settings/PaymentSettingsForm';
import NotificationSettingsForm from '../../components/settings/NotificationSettingsForm';
import ApiIntegrationForm from '../../components/settings/ApiIntegrationForm';
import { Settings, Clock, CreditCard, Bell, Database, Shield } from 'lucide-react';

const SettingsPage = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('dispatch');
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState({});
    const [fleets, setFleets] = useState([]);
    const [selectedFleetId, setSelectedFleetId] = useState(user.user_type === 'admin' ? '' : user.fleet_id);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const isAdmin = user.user_type === 'admin';

    useEffect(() => {
        fetchSettings();
        if (isAdmin) {
            fetchFleets();
        }
    }, [activeTab, selectedFleetId]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const settingsData = await settingService.getSettings(
                selectedFleetId || null,
                activeTab
            );
            setSettings(settingsData);
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    const handleFleetChange = (e) => {
        setSelectedFleetId(e.target.value);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleSettingsSave = async (updatedSettings) => {
        setIsSaving(true);
        setSaveSuccess(false);
        try {
            await settingService.updateSettings({
                settings: updatedSettings,
                fleet_id: selectedFleetId || null,
                setting_group: activeTab,
                updated_by: user.user_id
            });

            setSaveSuccess(true);
            fetchSettings(); // 重新加載設置

            // 3秒後隱藏成功提示
            setTimeout(() => {
                setSaveSuccess(false);
            }, 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
        } finally {
            setIsSaving(false);
        }
    };

    // 根據當前標籤返回對應的表單組件
    const getFormComponent = () => {
        const formProps = {
            settings: settings,
            onSave: handleSettingsSave,
            isAdmin: isAdmin,
            isLoading: isLoading,
            isSaving: isSaving
        };

        switch (activeTab) {
            case 'dispatch':
                return <DispatchSettingsForm {...formProps} />;
            case 'payment':
                return <PaymentSettingsForm {...formProps} />;
            case 'notification':
                return <NotificationSettingsForm {...formProps} />;
            case 'api':
                return <ApiIntegrationForm {...formProps} />;
            default:
                return <DispatchSettingsForm {...formProps} />;
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">系統設置</h1>
                {isAdmin && (
                    <div className="flex items-center">
                        <label className="mr-2 text-sm font-medium text-gray-700">車隊設置:</label>
                        <select
                            value={selectedFleetId}
                            onChange={handleFleetChange}
                            className="border border-gray-300 rounded-md px-3 py-2"
                        >
                            <option value="">全局設置</option>
                            {fleets.map(fleet => (
                                <option key={fleet.fleet_id} value={fleet.fleet_id}>
                                    {fleet.fleet_name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* 左側選單 */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                            <h3 className="font-bold">設置類別</h3>
                        </div>
                        <div className="divide-y divide-gray-100">
                            <button
                                className={`w-full px-4 py-3 text-left font-medium flex items-center ${activeTab === 'dispatch'
                                        ? 'bg-blue-50 border-l-4 border-blue-500 text-blue-600'
                                        : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                onClick={() => handleTabChange('dispatch')}
                            >
                                <Clock className="w-5 h-5 mr-3" />
                                派車邏輯設置
                            </button>
                            <button
                                className={`w-full px-4 py-3 text-left font-medium flex items-center ${activeTab === 'payment'
                                        ? 'bg-blue-50 border-l-4 border-blue-500 text-blue-600'
                                        : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                onClick={() => handleTabChange('payment')}
                            >
                                <CreditCard className="w-5 h-5 mr-3" />
                                計價規則設置
                            </button>
                            <button
                                className={`w-full px-4 py-3 text-left font-medium flex items-center ${activeTab === 'notification'
                                        ? 'bg-blue-50 border-l-4 border-blue-500 text-blue-600'
                                        : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                onClick={() => handleTabChange('notification')}
                            >
                                <Bell className="w-5 h-5 mr-3" />
                                通知設置
                            </button>
                            {isAdmin && (
                                <button
                                    className={`w-full px-4 py-3 text-left font-medium flex items-center ${activeTab === 'api'
                                            ? 'bg-blue-50 border-l-4 border-blue-500 text-blue-600'
                                            : 'hover:bg-gray-50 text-gray-700'
                                        }`}
                                    onClick={() => handleTabChange('api')}
                                >
                                    <Database className="w-5 h-5 mr-3" />
                                    API 集成
                                </button>
                            )}
                        </div>
                    </div>

                    {/* 設置說明 */}
                    <div className="mt-4 bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <Shield className="w-5 h-5 text-yellow-500" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-yellow-800">設置提示</h3>
                                <div className="mt-2 text-sm text-yellow-700">
                                    <p>
                                        {isAdmin && selectedFleetId === ''
                                            ? '您正在編輯全局設置，這將作為所有車隊的默認設置。車隊可以擁有自己的設置來覆蓋全局設置。'
                                            : '這些設置將僅應用於當前選擇的車隊。'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 右側設置表單 */}
                <div className="lg:col-span-3">
                    {/* 成功保存提示 */}
                    {saveSuccess && (
                        <div className="mb-4 bg-green-50 p-4 rounded-lg border border-green-100">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-green-800">
                                        設置已成功保存！
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 設置表單 */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="font-bold">{
                                activeTab === 'dispatch' ? '派車邏輯設置' :
                                    activeTab === 'payment' ? '計價規則設置' :
                                        activeTab === 'notification' ? '通知設置' :
                                            activeTab === 'api' ? 'API 集成' : ''
                            }</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                {
                                    activeTab === 'dispatch' ? '調整系統如何分配訂單給司機' :
                                        activeTab === 'payment' ? '設定各種車型和區域的計價規則' :
                                            activeTab === 'notification' ? '設定系統推送給用戶的通知規則' :
                                                activeTab === 'api' ? '管理第三方API集成與權限' : ''
                                }
                            </p>
                        </div>

                        {isLoading ? (
                            <div className="p-6 flex justify-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                            </div>
                        ) : (
                            <div className="p-6">
                                {getFormComponent()}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;